import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Progress,
  Text,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { FeedbackForm } from "./FeedbackForm";
import { TeamModal } from "./TeamModal";
import { AssetConfirmationCard } from "./AssetConfirmationCard";
import {
  InductionData,
  InductionFeedback,
  InductionSection,
} from "../Types";
import { useSufiVoice } from "../hooks/useSufiVoice";
import { PauseIcon, PlayIcon, VolumeIcon } from "../Token";
import { buildSegments } from "../services/narration";
import { InductionVideo } from "./InductionVideo";
import { InductionQuickTools } from "./InductionQuickTools";

import SufiAssistant from "../../../../assets/images/sufi-assistant.svg";
import SufiImage from "../../../../assets/images/sufi.png";
import CommonImage from "../../../../assets/images/common-icon.svg";
import MyDiv from"./InductionExperience.style"
import { baseUrl } from "../../../../hooks/useFetch";

interface InductionPlayerProps {
  data: InductionData;
  section: InductionSection;
  index: number;
  lastIndex: number;
  percent: number;
  submitting: boolean;
  error: string | null;
  onSelect: (index: number) => void;
  onNext: () => void;
  onPrev: () => void;
  onFinish: (feedback: InductionFeedback) => void;
  /**
   * Connect this to your backend AI/chat endpoint for generated answers.
   * When omitted, the panel searches the induction content locally.
   */
  onAskAssistant?: (
    question: string,
    context: { companyName?: string; employeeName?: string; sections: InductionSection[]; activeSection: InductionSection }
  ) => Promise<string>;
}


const SECTION_TO_CONFIG_TYPE: Record<string, string> = {
  company: "ORGANIZATION",
  branch: "BRANCH",
  business: "BUSINESS",
  department: "DEPARTMENT",
  policies: "POLICY",
  feedback: "POLICY",
};

function renderHighlightedWords(
  text: string,
  isCurrentSegment: boolean,
  activeCharIndex?: number | null
) {
  if (!text) return null;
  if (!isCurrentSegment || activeCharIndex == null) return text;

  const tokens = text.split(/(\s+)/);
  let cursor = 0;

  return (
    <>
      {tokens.map((token, tokenIdx) => {
        const start = cursor;
        const end = cursor + token.length;
        cursor = end;
        if (/^\s+$/.test(token)) return token;

        const active = activeCharIndex >= start && activeCharIndex < end;
        const read = end <= activeCharIndex;

        return (
          <Box
            as="span"
            key={tokenIdx}
            className={active ? "sufi-word-active" : read ? "sufi-word-read" : undefined}
          >
            {token}
          </Box>
        );
      })}
    </>
  );
}

function getVideoUrl(section: InductionSection, data: InductionData) {
  if (section.videoUrl) {
    if (/^(https?:\/\/|blob:)/i.test(section.videoUrl)) return section.videoUrl;
    const cleanPath = section.videoUrl.replace(/^\/?(api\/)?/, "");
    const base = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    return `${base}${cleanPath}`;
  }

  const type = SECTION_TO_CONFIG_TYPE[section.key] || "ORGANIZATION";
  return `${baseUrl}InductionPolicyConfig/step-video/stream/${type}?companyId=${data.companyId ?? ""}&branchId=${data.branchId ?? ""}&departmentId=${data.departmentId ?? ""}`;
}

function getSceneEyebrow(section: InductionSection, index: number, total: number) {
  return section.kicker || `CHAPTER ${String(index + 1).padStart(2, "0")} / ${String(total).padStart(2, "0")}`;
}


export function InductionPlayer({
  data,
  section,
  index,
  lastIndex,
  percent,
  submitting,
  error,
  onSelect,
  onFinish,
  onAskAssistant,
}: InductionPlayerProps) {
  const [playing, setPlaying] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [sectionReadDone, setSectionReadDone] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [selectedTeamLead, setSelectedTeamLead] = useState<{ id?: number; name?: string } | null>(null);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"sufi" | "video">("sufi");
  const wasPlayingBeforeVideo = useRef(true);
  const sceneRefs = useRef<Array<HTMLElement | null>>([]);
  const autoScrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const manualNavigationRef = useRef(false);
  const sections = data.sections || [];
  const segments = useMemo(() => buildSegments(section), [section]);

  const clearAutoScroll = useCallback(() => {
    if (autoScrollTimer.current) {
      clearTimeout(autoScrollTimer.current);
      autoScrollTimer.current = null;
    }
  }, []);

  const scrollToScene = useCallback(
    (targetIndex: number, behavior: ScrollBehavior = "smooth") => {
      const target = Math.max(0, Math.min(targetIndex, lastIndex));
      const targetElement = sceneRefs.current[target];
      const container = document.querySelector(
        ".induction-experience"
      ) as HTMLElement | null;

      if (!targetElement || !container) return;

      manualNavigationRef.current = true;
      clearAutoScroll();

      const startPosition = container.scrollTop;
      const targetPosition =
        startPosition +
        targetElement.getBoundingClientRect().top -
        container.getBoundingClientRect().top;

      const distance = targetPosition - startPosition;

      // Short, smooth transition
      const duration = 900;

      if (behavior === "auto" || Math.abs(distance) < 2) {
        container.scrollTop = targetPosition;
        onSelect(target);

        window.setTimeout(() => {
          manualNavigationRef.current = false;
        }, 100);

        return;
      }

      let startTime: number | null = null;

      const animateScroll = (currentTime: number) => {
        if (startTime === null) startTime = currentTime;

        const progress = Math.min(
          (currentTime - startTime) / duration,
          1
        );

        // Smooth ease-in-out
        const easedProgress =
          progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        container.scrollTop =
          startPosition + distance * easedProgress;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          container.scrollTop = targetPosition;
          onSelect(target);

          window.setTimeout(() => {
            manualNavigationRef.current = false;
          }, 150);
        }
      };

      requestAnimationFrame(animateScroll);
    },
    [clearAutoScroll, lastIndex, onSelect]
  );

  const handleNarrationDone = useCallback(() => {
    setSectionReadDone(true);
    if (index >= lastIndex) return;

    clearAutoScroll();
    autoScrollTimer.current = setTimeout(() => {
      scrollToScene(index + 1, "smooth");
    }, 1100);
  }, [clearAutoScroll, index, lastIndex, scrollToScene]);

  const {
    speaking,
    supported,
    activeId,
    isCompleted,
    activeCharIndex,
  } = useSufiVoice({
    segments,
    voiceEnabled,
    playing,
    onDone: handleNarrationDone,
  });

  // useEffect(() => {
  //   setSectionReadDone(false);
  //   setPlaying(true);
  // }, [index, section.key]);
  useEffect(() => {
    setSectionReadDone(false);
    setPlaying(true);
    setViewMode("sufi");
  }, [index, section.key]);

  // A deliberate user gesture cancels a pending auto-advance.
  useEffect(() => {
    const cancelPendingAdvance = () => {
      if (autoScrollTimer.current) {
        clearAutoScroll();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (["ArrowDown", "PageDown", " ", "ArrowUp", "PageUp", "Home", "End"].includes(event.key)) {
        cancelPendingAdvance();
      }
    };
    window.addEventListener("wheel", cancelPendingAdvance, { passive: true });
    window.addEventListener("touchstart", cancelPendingAdvance, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("wheel", cancelPendingAdvance);
      window.removeEventListener("touchstart", cancelPendingAdvance);
      window.removeEventListener("keydown", onKeyDown);
      clearAutoScroll();
    };
  }, [clearAutoScroll]);



  // Keep the active chapter in sync with ordinary document scrolling.
  useEffect(() => {
    const nodes = sceneRefs.current.filter(Boolean) as HTMLElement[];
    if (!nodes.length || !("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible || manualNavigationRef.current) return;

        const nextIndex = Number((visible.target as HTMLElement).dataset.sceneIndex);
        if (Number.isInteger(nextIndex) && nextIndex !== index) {
          clearAutoScroll();
          onSelect(nextIndex);
        }
      },
      { threshold: [0.01, 0.2, 0.4], rootMargin: "-10% 0px -10% 0px" }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [sections.length, index, onSelect, clearAutoScroll]);

  useEffect(() => {
    if (!activeId) return;
    document.getElementById(`induction-seg-${activeId}`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeId]);

  const currentText =
    segments.find((segment) => segment.id === activeId)?.text ??
    section.narration ??
    section.intro;

  useEffect(() => {
    if (index <= 0) return;
    manualNavigationRef.current = true;
    const initialScroll = window.setTimeout(() => {
      sceneRefs.current[index]?.scrollIntoView({ behavior: "auto", block: "start" });
      window.setTimeout(() => {
        manualNavigationRef.current = false;
      }, 900);
    }, 80);
    return () => window.clearTimeout(initialScroll);
    // The player mounts once after the welcome screen; restore the saved chapter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const status = !playing
    ? "Paused"
    : !voiceEnabled
      ? "Reading silently"
      : !supported
        ? "Reading"
        : speaking
          ? "Speaking"
          : sectionReadDone || isCompleted
            ? "Chapter complete"
            : "Preparing";

  // if (showVideo) {
  //   return (
  //     <InductionVideo
  //       onClose={() => setShowVideo(false)}
  //       videoUrl={getVideoUrl(section, data)}
  //       stepTitle={section.title}
  //     />
  //   );
  // }

  const openVideoView = () => {
    wasPlayingBeforeVideo.current = playing;
    setPlaying(false);
    clearAutoScroll();
    setViewMode("video");
  };

  const openSufiView = () => {
    setViewMode("sufi");

    if (wasPlayingBeforeVideo.current && !sectionReadDone) {
      setPlaying(true);
    }
  };


  return (
    <MyDiv>
    <main className="induction-experience" aria-label="Employee induction">
      <div className="induction-progress-rail" aria-hidden="true">
        <div className="induction-progress-fill" style={{ width: `${percent}%` }} />
      </div>

      {sections.map((scene, sceneIndex) => {
        const isActiveScene = sceneIndex === index;
        const isSceneFeedback = sceneIndex === lastIndex || scene.key === "feedback";
        // const sceneHasVideo = !!(scene.hasVideo || scene.videoUrl);
        const sceneHasVideo = scene.key !== "feedback";
        const isPast = sceneIndex < index;

        return (
          <section
            key={`${scene.key}-${sceneIndex}`}
            ref={(node) => { sceneRefs.current[sceneIndex] = node; }}
            data-scene-index={sceneIndex}
            className={`induction-scene induction-scene-${scene.key || "chapter"} ${isActiveScene ? "is-active" : ""}`}
            aria-label={`${scene.label || scene.title} section`}
          >
            <div className="scene-grain" aria-hidden="true" />
            <header className="scene-topbar">
              <div className="scene-brand">
                <span className="scene-brand-mark">T</span>
                <span className="scene-brand-name">{data.companyName || "TEK Inspirations LLC"}</span>
                <span className="scene-brand-divider" />
                <span className="scene-brand-context">PEOPLE · CULTURE · GROWTH</span>
              </div>

              {/* <span className="scene-chapter-count">
                  {String(sceneIndex + 1).padStart(2, "0")} <i>/</i> {String(sections.length).padStart(2, "0")}
                </span> */}
              {/* {sceneHasVideo && isActiveScene && (
                  <Button
                    size="sm"
                    onClick={() => setShowVideo(true)}
                    className="scene-video-button"
                    leftIcon={<PlayIcon boxSize={3} />}
                  >
                    Watch chapter
                    {scene.videoDurationFormatted ? ` · ${scene.videoDurationFormatted}` : ""}
                  </Button>
                )} */}
              <div className="scene-topbar-right">
                <span className="scene-chapter-count">
                  {String(sceneIndex + 1).padStart(2, "0")}
                  <i>/</i>
                  {String(sections.length).padStart(2, "0")}
                </span>

                {isActiveScene && sceneHasVideo && (
                  <div className="induction-view-toggle">
                    <button type="button" className={viewMode === "sufi" ? "is-active" : ""} onClick={openSufiView}>
                      <span>✦</span>
                      With Sufi
                    </button>

                    <button type="button" className={viewMode === "video" ? "is-active" : ""} onClick={openVideoView}>
                      <PlayIcon boxSize={3} />
                      Watch Video
                    </button>
                  </div>
                )}
              </div>

            </header>
            {viewMode === "sufi" ? (
              <div className="scene-layout">
                <div className="scene-copy">
                  <div className="scene-eyebrow">
                    <span className="scene-eyebrow-line" />
                    {getSceneEyebrow(scene, sceneIndex, sections.length)}
                  </div>

                  <div className="scene-heading-wrap">
                    <span className="scene-oversized-number">{String(sceneIndex + 1).padStart(2, "0")}</span>
                    <Heading as="h1" className="scene-title">
                      {scene.title || scene.label}
                    </Heading>
                  </div>

                  {scene.intro && (
                    <Text className="scene-intro" id={isActiveScene ? "induction-seg-intro" : undefined}>
                      {renderHighlightedWords(
                        scene.intro,
                        isActiveScene && activeId === "intro",
                        activeCharIndex
                      )}
                    </Text>
                  )}


                  {scene.stats?.length > 0 && (
                    <div className="scene-stats">
                      {scene.stats.map((stat, statIndex) => (
                        <div
                          className={`scene-stat ${isActiveScene && activeId === `stat-${statIndex}` ? "is-speaking" : ""}`}
                          id={isActiveScene ? `induction-seg-stat-${statIndex}` : undefined}
                          key={`${stat.label}-${statIndex}`}
                        >
                         <Flex className="branch-head-card-content" gap={0}>
  {["branch head", "department head"].includes(
    stat.label?.trim().toLowerCase() || ""
  ) && (
    <Image
      src={data.branchHeadImage || SufiImage}
      alt={stat.label}
      className="branch-head-image"
      objectFit="cover"
      fallbackSrc={SufiImage}
    />
  )}

  <Box className="branch-head-info">
    <span className="scene-stat-value">
      {renderHighlightedWords(
        stat.value,
        isActiveScene && activeId === `stat-${statIndex}`,
        activeCharIndex
      )}
    </span>
    <span className="scene-stat-label">{stat.label}</span>
  </Box>
</Flex>
                        </div>
                      ))}
                    </div>
                  )}

                  {scene.items?.length > 0 && (
                    <div className="scene-items">
                      {scene.items.map((item, itemIndex) => {
                        const isTeamItem =
                          scene.key === "department" || item.isClickable;

                        const isItemSpeaking =
                          isActiveScene && activeId === `item-${itemIndex}`;

                        const leadName =
                          item.teamLeadName ||
                          item.teamLead ||
                          item.label.replace(/^Team\s+/i, "");

                        return (
                          <button
                            type="button"
                            key={`${item.label}-${itemIndex}`}
                            className={`scene-item ${isItemSpeaking ? "is-speaking" : ""} ${isTeamItem ? "is-interactive scene-team-item" : ""
                              }`}
                            id={isActiveScene ? `induction-seg-item-${itemIndex}` : undefined}
                            onClick={() => {
                              if (!isTeamItem) return;

                              setSelectedTeamLead({
                                id: item.teamLeadId,
                                name: leadName,
                              });
                              setIsTeamModalOpen(true);
                            }}
                          >
                            {isTeamItem ? (
                              <Avatar name={leadName} src={item.teamLeadImage || SufiImage} size="md" flexShrink={0}/>
                            ) : (
                                <span className="scene-item-index">
                              <Image  src={item.imageUrl || CommonImage} fallbackSrc={CommonImage}  alt={item.label}  boxSize="20px" objectFit="cover" borderRadius="12px" flexShrink={0}/>
                              </span>
                            )}

                            <span className="scene-item-copy">
                              <strong>
                                {renderHighlightedWords(
                                  item.label,
                                  isItemSpeaking,
                                  activeCharIndex
                                )}
                              </strong>

                              <span>
                                {renderHighlightedWords(
                                  item.text,
                                  isItemSpeaking,
                                  activeCharIndex == null ? null : Math.max(0, activeCharIndex - item.label.length - 2))}
                              </span>
                            </span>

                            {isTeamItem && (
                              <span className="scene-item-action">
                                Explore team ↗
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {data.assignedAsset?.hasAsset && (scene.key === "policies" || scene.key === "department") && (
                    <div className="scene-asset-wrap">
                      <AssetConfirmationCard asset={data.assignedAsset} />
                    </div>
                  )}

                  {isSceneFeedback && (
                    <div className="scene-feedback">

                      <FeedbackForm submitting={submitting} onSubmit={onFinish} />
                    </div>
                  )}

                  {isActiveScene && error && (
                    <Alert status="error" borderRadius="12px" mt={4}>
                      <AlertIcon />
                      {error}
                    </Alert>
                  )}

                  <div className="scene-bottomline">
                    <span>{data.employeeName ? `A journey created for ${data.employeeName}` : "Your first chapter starts here"}</span>
                    <span>{isPast ? "COMPLETED" : isActiveScene ? "NOW EXPLORING" : "UP NEXT"}</span>
                  </div>
                </div>

                <aside className={`scene-guide ${isActiveScene && speaking ? "is-speaking" : ""}`}>
                  <div className="guide-orbit guide-orbit-one" />
                  <div className="guide-orbit guide-orbit-two" />
                  <div className="guide-glow" />
                  <div className="guide-image-wrap">
                    <Image
                      src={SufiAssistant}
                      alt="Sufi, your AI induction guide"
                      className="guide-avatar"
                      draggable={false}
                    />
                  </div>
                  <div className="guide-caption">
                    <div className="guide-caption-top">
                      <div>
                        <strong>Sufi</strong>
                        <span>YOUR AI GUIDE</span>
                      </div>
                      <span className={`guide-live-status ${isActiveScene && speaking ? "is-live" : ""}`}>
                        <i />
                        {isActiveScene ? status : isPast ? "Chapter covered" : "Waiting"}
                      </span>
                    </div>
                    <p>
                      {isActiveScene
                        ? renderHighlightedWords(currentText, true, activeCharIndex)
                        : scene.narration || scene.intro || "I'll guide you through this part of your journey."}
                    </p>
                    <div className="guide-waveform" aria-label={isActiveScene && speaking ? "Sufi is speaking" : "Sufi is ready"}>
                      {Array.from({ length: 27 }).map((_, waveIndex) => (
                        <i
                          key={waveIndex}
                          style={{ "--wave-delay": `${waveIndex * 35}ms` } as CSSProperties & { "--wave-delay": string }}
                        />
                      ))}
                    </div>
                    {isActiveScene && (
                      <div className="guide-controls">
                        <button
                          type="button"
                          className="guide-control-primary"
                          onClick={() => setPlaying((current) => !current)}
                          aria-label={playing ? "Pause narration" : "Resume narration"}
                        >
                          {playing ? <PauseIcon boxSize={3.5} /> : <PlayIcon boxSize={3.5} />}
                          {playing ? "Pause" : "Resume"}
                        </button>
                        <button
                          type="button"
                          className={`guide-control-secondary ${voiceEnabled ? "" : "is-muted"}`}
                          onClick={() => setVoiceEnabled((current) => !current)}
                          aria-label={voiceEnabled ? "Mute Sufi" : "Enable Sufi voice"}
                        >
                          <VolumeIcon boxSize={3.5} muted={!voiceEnabled} />
                          {voiceEnabled ? "Voice on" : "Voice off"}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="guide-nameplate">
                    <span className="guide-nameplate-dot" />
                    <span>SUFI · HUMAN RESOURCES ASSISTANT</span>
                  </div>
                </aside>
              </div>

            ) : (
              <div className="scene-video-view">
                <InductionVideo
                  onClose={openSufiView}
                  videoUrl={getVideoUrl(scene, data)}
                  stepTitle={scene.title || scene.label}
                />
              </div>
            )}
            {viewMode === "sufi" && (
              <footer className="scene-footer">
                <div className="scene-footer-left">
                  <span className="scene-footer-progress">
                    {Math.round(
                      ((sceneIndex + 1) / Math.max(sections.length, 1)) * 100
                    )}%
                  </span>
                  <span>OF YOUR INDUCTION JOURNEY</span>
                </div>

                <div className="scene-pagination" aria-label="Choose induction chapter">
                  {sections.map((chapter, chapterIndex) => (
                    <button
                      type="button"
                      key={`${chapter.key}-${chapterIndex}`}
                      aria-label={`Go to ${chapter.label}`}
                      aria-current={chapterIndex === index ? "step" : undefined}
                      className={`
            ${chapterIndex === index ? "is-current" : ""}
            ${chapterIndex < index ? "is-done" : ""}
          `}
                      onClick={() => scrollToScene(chapterIndex)}
                    />
                  ))}
                </div>

                <div className="scene-footer-actions">
                  <button
                    type="button"
                    className="scene-skip-button"
                    onClick={() => scrollToScene(Math.max(0, sceneIndex - 1))}
                    disabled={sceneIndex === 0}
                  >
                    Previous
                  </button>

                  {!isSceneFeedback && (
                    <button
                      type="button"
                      className="scene-next-button"
                      onClick={() => scrollToScene(sceneIndex + 1)}
                    >
                      {isActiveScene && sectionReadDone ? "Continue" : "Explore next"}
                      <span>↗</span>
                    </button>
                  )}
                </div>
              </footer>
            )}

          </section>
        );
      })}

      <InductionQuickTools
        companyId={data.companyId}
        employeeName={data.employeeName}
        companyName={data.companyName}
        sections={sections}
        activeSection={section}
        onAskAssistant={onAskAssistant}
      />

      <TeamModal
        isOpen={isTeamModalOpen}
        onClose={() => setIsTeamModalOpen(false)}
        teamLeadId={selectedTeamLead?.id}
        teamLeadName={selectedTeamLead?.name}
        departmentName={data.departmentName}
        departmentId={data.departmentId}
      />
    </main>
    </MyDiv>
  );
}
